// src/components/AGTFit/goals/CheckIns.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Divider,
  IconButton,
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Plus,
  ChevronRight,
  ChevronLeft,
  Camera,
  Info,
  X,
  Edit,
  Trash2
} from 'lucide-react';
import { format, parse } from 'date-fns';
import Carousel from 'react-material-ui-carousel';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import agtfitService from '../service/agtfitService';
import { debounce } from 'lodash';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Tarjeta de Check-In mejorada
const CheckInCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isAdd'
})(({ theme, isAdd }) => ({
  borderRadius: 20,
  height: '100%',
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: isAdd ? 'pointer' : 'default',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? (isAdd ? 'rgba(255, 107, 0, 0.15)' : WALLPAPER_COLORS.bg)
    : (isAdd
      ? (theme.palette.mode === 'dark'
        ? 'rgba(255, 107, 0, 0.1)'
        : 'rgba(255, 107, 0, 0.05)')
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 32, 0.8)'
        : '#FFFFFF')),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${isAdd ? FIT_ORANGE : WALLPAPER_COLORS.border}`
    : `1px solid ${isAdd ? FIT_ORANGE : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  transition: 'all 0.2s',
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? 'none'
    : (isAdd ? 'none' : theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(0,0,0,0.3)'
      : '0 4px 12px rgba(0,0,0,0.05)'),
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? (isAdd ? 'rgba(255, 107, 0, 0.2)' : WALLPAPER_COLORS.bgLight)
      : (isAdd
        ? (theme.palette.mode === 'dark'
          ? 'rgba(255, 107, 0, 0.15)'
          : 'rgba(255, 107, 0, 0.1)')
        : (theme.palette.mode === 'dark'
          ? 'rgba(40, 40, 42, 0.8)'
          : '#FFFFFF')),
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (isAdd ? 'none' : theme.palette.mode === 'dark'
        ? '0 6px 16px rgba(0,0,0,0.4)'
        : '0 6px 16px rgba(0,0,0,0.08)'),
  }
}));

// Círculo para el icono de añadir
const AddCircle = styled(Box)(({ theme }) => ({
  width: 70,
  height: 70,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #FF9800 0%, #FF6B00 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#FFFFFF',
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 10px rgba(255, 107, 0, 0.3)',
}));

// Botón de navegación del carrusel actualizado para mejor visibilidad
const CarouselNavButton = styled(IconButton)(({ theme, direction }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? 'rgba(30, 30, 32, 0.8)' : 'rgba(255, 255, 255, 0.8)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.15)',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  // Hacer los botones más visibles y accesibles
  ...(direction === 'left' ? { left: 0 } : { right: 0 }),
  opacity: 0.9,
  padding: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(40, 40, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)'),
    opacity: 1,
  },
}));

// Estilo para diálogos estilo Apple
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? '0 20px 60px rgba(0, 0, 0, 0.5)'
        : '0 20px 60px rgba(0, 0, 0, 0.2)'),
    background: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 32, 0.95)'
        : 'rgba(255, 255, 255, 1)'),
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    overflow: 'hidden'
  }
}));

// Estilo para botones en diálogos
const DialogButton = styled(Box)(({ theme, primary }) => ({
  borderRadius: 12,
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...(primary && {
    background: 'linear-gradient(135deg, #FF9800 0%, #FF6B00 100%)',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#E65100',
      boxShadow: '0 4px 12px rgba(255, 107, 0, 0.3)',
    },
  }),
  ...(!primary && {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(70, 70, 72, 0.8)' : 'rgba(230, 230, 235, 0.8)',
    },
  }),
}));

// Estilo para inputs en diálogos
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    '& fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? WALLPAPER_COLORS.text
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
    },
    '&.Mui-focused fieldset': {
      borderColor: FIT_ORANGE,
    },
  },
}));

// Estilo para selects en diálogos
const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.text
      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: FIT_ORANGE,
  },
}));

const CheckIns = ({ checkIns, loading, onAddCheckIn, onUpdateCheckIn, onDeleteCheckIn }) => {
  const theme = useTheme();
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkInDate, setCheckInDate] = useState(new Date());
  const [checkInWeight, setCheckInWeight] = useState('');
  const [checkInPhoto, setCheckInPhoto] = useState(null);
  const [bodyFatMethod, setBodyFatMethod] = useState('direct');
  const [bodyFatPercentage, setBodyFatPercentage] = useState('');
  const [measurements, setMeasurements] = useState({
    neck: '',
    waist: '',
    hip: '',
    chest: '',
    arm: '',
    thigh: ''
  });
  const [editingCheckIn, setEditingCheckIn] = useState(null);
  const [profile, setProfile] = useState(null);

  // Cargar el perfil del usuario al montar el componente
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profileResponse = await agtfitService.getProfile();
        if (profileResponse.success && profileResponse.data) {
          setProfile(profileResponse.data);
        }
      } catch (error) {
        console.error("Error loading profile:", error);
      }
    };
    
    loadProfile();
  }, []);

  // Función para calcular el porcentaje de grasa corporal
  const calculateBodyFat = () => {
    if (bodyFatMethod === 'direct') {
      // No hacer cálculos para medición directa
      return;
    }
    
    if (bodyFatMethod === 'navy') {
      // Método Navy
      // Verificar que tenemos los valores necesarios
      if (!measurements.neck || !measurements.waist || 
          (profile?.gender === 'female' && !measurements.hip)) {
        return; // No hay datos suficientes para calcular
      }
      
      const neck = parseFloat(measurements.neck);
      const waist = parseFloat(measurements.waist);
      
      if (profile?.gender === 'female') {
        const hip = parseFloat(measurements.hip);
        if (isNaN(neck) || isNaN(waist) || isNaN(hip)) return;
        
        // Fórmula Navy para mujeres
        const logBase = Math.log10(waist + hip - neck);
        const bodyFat = 163.205 * logBase - 97.684 * Math.log10(profile.height) - 78.387;
        setBodyFatPercentage(bodyFat.toFixed(1));
      } else {
        if (isNaN(neck) || isNaN(waist)) return;
        
        // Fórmula Navy para hombres
        const logBase = Math.log10(waist - neck);
        const bodyFat = 86.01 * logBase - 70.041 * Math.log10(profile.height) + 36.76;
        setBodyFatPercentage(bodyFat.toFixed(1));
      }
    } else if (bodyFatMethod === 'jackson') {
      // Método Jackson-Pollock
      // Para este método necesitamos diferentes pliegues cutáneos
      // Como ejemplo simplificado, usaremos las medidas disponibles
      if (!measurements.waist || !measurements.hip || !measurements.thigh) {
        return; // No hay datos suficientes para calcular
      }
      
      const waist = parseFloat(measurements.waist);
      const hip = parseFloat(measurements.hip);
      const thigh = parseFloat(measurements.thigh);
      
      if (isNaN(waist) || isNaN(hip) || isNaN(thigh)) return;
      
      // Fórmula simplificada de Jackson-Pollock
      // Esta es una aproximación, la fórmula real requiere pliegues cutáneos
      let bodyFat;
      if (profile?.gender === 'female') {
        bodyFat = 0.29669 * (waist + hip + thigh) - 0.00043 * Math.pow(waist + hip + thigh, 2) + 0.02963 * (profile.age || 30) + 1.4072;
      } else {
        bodyFat = 0.1051 * (waist + hip) + 0.02963 * (profile.age || 30) + 1.0938;
      }
      
      setBodyFatPercentage(bodyFat.toFixed(1));
    }
  };

  // Crear función con debounce para cálculo de grasa corporal
  const debouncedCalculateBodyFat = useRef(
    debounce(() => {
      calculateBodyFat();
    }, 300) // 300ms de debounce
  ).current;

  // Modificar el efecto para usar la función con debounce
  useEffect(() => {
    debouncedCalculateBodyFat();

    return () => {
      debouncedCalculateBodyFat.cancel();
    };
  }, [bodyFatMethod, measurements.neck, measurements.waist, measurements.hip, measurements.thigh, profile, debouncedCalculateBodyFat]);

  const handleOpenCheckInDialog = () => {
    // Resetear formulario para nuevo check-in
    setEditingCheckIn(null);
    setCheckInDate(new Date());
    setCheckInWeight('');
    setBodyFatPercentage('');
    setBodyFatMethod('direct');
    setMeasurements({
      neck: '',
      waist: '',
      hip: '',
      chest: '',
      arm: '',
      thigh: ''
    });
    setCheckInPhoto(null);
    setCheckInDialogOpen(true);
  };

  const handleCloseCheckInDialog = () => {
    setCheckInDialogOpen(false);
    setEditingCheckIn(null);
  };

  const handleMeasurementChange = (field, value) => {
    setMeasurements(prev => ({
      ...prev,
      [field]: value
    }));
    // El cálculo se realizará automáticamente gracias al useEffect con debounce
  };

  const handleSaveCheckIn = () => {
    // Obtener la fecha en formato yyyy-MM-dd directamente del objeto Date
    // Esto evita cualquier conversión de zona horaria al usar toISOString()
    const year = checkInDate.getFullYear();
    const month = String(checkInDate.getMonth() + 1).padStart(2, '0');
    const day = String(checkInDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
  
    const checkInDataToSave = {
      date: formattedDate, // Usar la fecha formateada directamente
      weight: parseFloat(checkInWeight),
      bodyFat: bodyFatPercentage ? parseFloat(bodyFatPercentage) : null,
      bodyFatMethod,
      measurements: Object.fromEntries(
        Object.entries(measurements).filter(([_, value]) => value !== '')
      )
    };
  
    onAddCheckIn(checkInDataToSave);
    
    // Limpiar el formulario
    setCheckInWeight('');
    setBodyFatPercentage('');
    setMeasurements({
      neck: '',
      waist: '',
      hip: '',
      chest: '',
      arm: '',
      thigh: ''
    });
    setCheckInPhoto(null);
    
    handleCloseCheckInDialog();
  };

  // Función para editar un check-in existente (corregida)
  const handleEditCheckIn = (checkIn) => {
    setEditingCheckIn(checkIn);
    
    // Corregir la creación de la fecha para evitar problemas de zona horaria
    // Usar la fecha exacta del check-in sin conversiones adicionales
    const [year, month, day] = checkIn.checkInDate.split('-');
    const checkInDateObj = new Date(year, month - 1, day);
    setCheckInDate(checkInDateObj);
    
    setCheckInWeight(checkIn.weight.toString());
    setBodyFatPercentage(checkIn.bodyFat ? checkIn.bodyFat.toString() : '');
    setBodyFatMethod(checkIn.bodyFatMethod || 'direct');
    
    // Configurar medidas si existen
    if (checkIn.measurements) {
      setMeasurements({
        neck: checkIn.measurements.neck ? checkIn.measurements.neck.toString() : '',
        waist: checkIn.measurements.waist ? checkIn.measurements.waist.toString() : '',
        hip: checkIn.measurements.hip ? checkIn.measurements.hip.toString() : '',
        chest: checkIn.measurements.chest ? checkIn.measurements.chest.toString() : '',
        arm: checkIn.measurements.arm ? checkIn.measurements.arm.toString() : '',
        thigh: checkIn.measurements.thigh ? checkIn.measurements.thigh.toString() : ''
      });
    } else {
      setMeasurements({
        neck: '',
        waist: '',
        hip: '',
        chest: '',
        arm: '',
        thigh: ''
      });
    }
    
    setCheckInDialogOpen(true);
  };

  // Función para guardar la edición
  const handleSaveEdit = () => {
    const year = checkInDate.getFullYear();
    const month = String(checkInDate.getMonth() + 1).padStart(2, '0');
    const day = String(checkInDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
  
    const updatedCheckInData = {
      checkInId: editingCheckIn.checkInId,
      date: formattedDate,
      weight: parseFloat(checkInWeight),
      bodyFat: bodyFatPercentage ? parseFloat(bodyFatPercentage) : null,
      bodyFatMethod,
      measurements: Object.fromEntries(
        Object.entries(measurements).filter(([_, value]) => value !== '')
      )
    };
    
    onUpdateCheckIn(updatedCheckInData);
    
    // Limpiar el formulario y estado de edición
    setEditingCheckIn(null);
    setCheckInWeight('');
    setBodyFatPercentage('');
    setMeasurements({
      neck: '',
      waist: '',
      hip: '',
      chest: '',
      arm: '',
      thigh: ''
    });
    setCheckInPhoto(null);
    
    handleCloseCheckInDialog();
  };

  // Función para eliminar un check-in
  const handleDeleteCheckIn = (checkInId) => {
    if (window.confirm('Are you sure you want to delete this check-in?')) {
      onDeleteCheckIn(checkInId);
    }
  };

  // Función para manejar el botón de guardar (crear o editar)
  const handleSaveButton = () => {
    if (editingCheckIn) {
      handleSaveEdit();
    } else {
      handleSaveCheckIn();
    }
  };

  // Renderizar carrusel de check-ins
  const renderCheckInCarousel = () => {
    // Ordenar los check-ins por fecha (más recientes primero)
    const sortedCheckIns = [...checkIns].sort((a, b) => 
      new Date(b.checkInDate) - new Date(a.checkInDate)
    );

    // Combinar la tarjeta de añadir con las tarjetas existentes
    const allItems = [
      <CheckInCard key="add" isAdd onClick={handleOpenCheckInDialog} id="add-check-in-button">
        <AddCircle>
          <Plus size={30} />
        </AddCircle>
        <Typography variant="subtitle1" fontWeight={600}>
          Add Check-In
        </Typography>
      </CheckInCard>,
      ...sortedCheckIns.map(checkIn => (
        <CheckInCard key={checkIn.checkInId}>
          <CardContent sx={{ width: '100%', position: 'relative' }}>
            {/* Botones de edición y eliminación */}
            <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCheckIn(checkIn);
                }}
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
                }}
              >
                <Edit size={16} />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCheckIn(checkIn.checkInId);
                }}
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
                  '&:hover': {
                    backgroundColor: theme.palette.error.dark,
                    color: '#fff'
                  }
                }}
              >
                <Trash2 size={16} />
              </IconButton>
            </Box>
            
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: FIT_ORANGE }}>
              {format(parse(checkIn.checkInDate, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Weight
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {checkIn.weight} kg
              </Typography>
            </Box>
            
            {checkIn.bodyFat && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Body Fat
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {checkIn.bodyFat}%
                </Typography>
              </Box>
            )}
            
            {checkIn.measurements && Object.keys(checkIn.measurements).length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Measurements
                </Typography>
                
                <Grid container spacing={1}>
                  {Object.entries(checkIn.measurements)
                    .filter(([_, value]) => value) // Filtrar valores vacíos
                    .map(([key, value]) => (
                      <Grid item xs={6} key={key}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {key}
                          </Typography>
                          <Typography variant="caption">
                            {value} cm
                          </Typography>
                        </Box>
                      </Grid>
                    ))
                  }
                </Grid>
              </>
            )}
          </CardContent>
        </CheckInCard>
      ))
    ];

    // Agrupar los elementos en pares para el carrusel
    const groupedItems = [];
    for (let i = 0; i < allItems.length; i += 2) {
      groupedItems.push(
        <Grid container spacing={2} key={i} sx={{ px: 1 }}>
          <Grid item xs={6}>
            {allItems[i]}
          </Grid>
          {allItems[i + 1] && (
            <Grid item xs={6}>
              {allItems[i + 1]}
            </Grid>
          )}
        </Grid>
      );
    }

    return (
      <Box sx={{ position: 'relative', mx: -2, pb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={40} sx={{ color: FIT_ORANGE }} />
          </Box>
        ) : groupedItems.length > 0 ? (
          <Carousel
            navButtonsAlwaysVisible={true}
            indicators={true}
            indicatorIconButtonProps={{
              style: {
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                margin: '0 4px',
              }
            }}
            activeIndicatorIconButtonProps={{
              style: {
                color: FIT_ORANGE,
              }
            }}
            indicatorContainerProps={{
              style: {
                marginTop: '16px',
              }
            }}
            animation="slide"
            autoPlay={false}
            cycleNavigation={false}
            NavButton={({ onClick, className, style, next, prev }) => {
              return (
                <CarouselNavButton
                  onClick={onClick}
                  className={className}
                  style={style}
                  direction={next ? 'right' : 'left'}
                  size="small"
                >
                  {next ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                </CarouselNavButton>
              );
            }}
            sx={{
              width: '100%',
            }}
          >
            {groupedItems}
          </Carousel>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              No check-ins yet. Click the + button to add your first check-in.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Determinar el título y texto del botón según si estamos editando o creando
  const dialogTitle = editingCheckIn ? 'Edit Check-In' : 'New Check-In';
  const saveButtonText = editingCheckIn ? 'Update' : 'Save';

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
        Check-Ins
      </Typography>
      
      {renderCheckInCarousel()}

      {/* Diálogo para añadir/editar check-in */}
      <StyledDialog 
        open={checkInDialogOpen} 
        onClose={handleCloseCheckInDialog}
        fullWidth
        maxWidth="md"
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}`
        }}>
          <Typography variant="h5" fontWeight={600}>
            {dialogTitle}
          </Typography>
          
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'row-reverse', sm: 'row' }, // Cambio de dirección en móvil
            alignItems: 'center',
            gap: 1
          }}>
            <IconButton 
              onClick={handleCloseCheckInDialog} 
              sx={{ 
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(70, 70, 72, 0.8)' : 'rgba(230, 230, 235, 0.8)',
                }
              }}
            >
              <X size={20} />
            </IconButton>
            
            <DialogButton
              variant="contained"
              primary={true}
              onClick={handleSaveButton}
              disabled={!checkInWeight} // Deshabilitar si no hay peso
              sx={{
                display: 'flex',
                whiteSpace: 'nowrap'
              }}
            >
              {saveButtonText}
            </DialogButton>
          </Box>
        </Box>
        
        <DialogContent sx={{ px: 3, py: 4 }}>
          <Grid container spacing={3}>
            {/* Fecha */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={checkInDate}
                  onChange={(newDate) => setCheckInDate(newDate)}
                  renderInput={(params) => <StyledTextField {...params} fullWidth />}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            {/* Peso */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Weight
              </Typography>
              <StyledTextField
                fullWidth
                value={checkInWeight}
                onChange={(e) => setCheckInWeight(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
              />
            </Grid>
            
            {/* Foto */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Photo
              </Typography>
              <DialogButton
                variant="outlined"
                component="label"
                sx={{ 
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                  color: theme.palette.text.primary,
                  display: 'inline-flex'
                }}
              >
                <Camera style={{ marginRight: 8 }} />
                Upload Photo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setCheckInPhoto(e.target.files[0])}
                />
              </DialogButton>
              {checkInPhoto && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {checkInPhoto.name}
                </Typography>
              )}
            </Grid>
            
            {/* Body Fat y Measurements */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                % Body Fat and Measurements
              </Typography>
              
              <Grid container spacing={3}>
                {/* Método de cálculo de grasa corporal */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="body-fat-method-label">Calculation % Fat</InputLabel>
                    <StyledSelect
                      labelId="body-fat-method-label"
                      value={bodyFatMethod}
                      onChange={(e) => setBodyFatMethod(e.target.value)}
                      label="Calculation % Fat"
                    >
                      <MenuItem value="direct">Direct Measurement</MenuItem>
                      <MenuItem value="navy">Navy Method</MenuItem>
                      <MenuItem value="jackson">Jackson-Pollock Method</MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Grid>
                
                {/* Porcentaje de grasa corporal */}
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    label="Fat %"
                    fullWidth
                    value={bodyFatPercentage}
                    onChange={(e) => setBodyFatPercentage(e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    // Deshabilitar el campo cuando no es medición directa
                    disabled={bodyFatMethod !== 'direct'}
                  />
                </Grid>
                
                {/* Medidas corporales */}
                {['neck', 'waist', 'hip', 'chest', 'arm', 'thigh'].map((field) => (
                  <Grid item xs={12} sm={6} key={field}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <StyledTextField
                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                        fullWidth
                        value={measurements[field]}
                        onChange={(e) => handleMeasurementChange(field, e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                        }}
                      />
                      <Tooltip title={`How to measure ${field}`}>
                        <IconButton sx={{ 
                          ml: 1,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
                        }}>
                          <Info size={18} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>
    </Box>
  );
};

export default CheckIns;